import { useState, useEffect, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { RxCross2 } from "react-icons/rx";
import {
    FaUser, FaShoppingCart, FaBox, FaWarehouse, FaEnvelope,
    FaPhoneAlt, FaMapMarkerAlt, FaHashtag, FaCity, FaFlag, FaRupeeSign,
    FaWallet, FaClipboardList, FaWeightHanging, FaBoxOpen, FaRulerCombined,
    FaChevronDown, FaCheckCircle, FaLock
} from 'react-icons/fa';
import { useMyContext } from '../../../../context/MyContext';
import '../../../../css/custom-scrollbar.css';
import ViewRateForword from '../../../../component/b2c/models/ViewRateForwordShipmet';
import { Base_Url, getpincode, addShipment, allwarehouseslist } from '../../../../config/config';
import { toast } from 'react-toastify';
import Spinner from '../../../../component/spinner/spinner';
import axios from 'axios';

const schema = z.object({
    warehouse: z.string().nonempty("Warehouse is required"),
    firstName: z
        .string().trim().min(1, "First Name is required")
        .regex(/^[a-zA-Z\s\-'\.]+$/, "First Name must only contain alphabetic characters")
        .refine((val) => val.trim().length > 0, { message: "First Name cannot be only spaces" }),
    lastName: z
        .string().trim().min(1, "Last Name is required")
        .regex(/^[a-zA-Z\s\-'\.]+$/, "Last Name must only contain alphabetic characters")
        .refine((val) => val.trim().length > 0, { message: "Last Name cannot be only spaces" }),
    email: z
        .string()
        .trim()
        .min(1, "Email is required")
        .email("Please enter a valid email address"),
    mobile: z
        .string().trim()
        .refine((val) => /^\d{10}$/.test(val), { message: "Mobile number must be exactly 10 digits" }),
    address: z
        .string().min(1, "Address is required")
        .regex(/^[^#\\%&;@]*$/, "Address contains invalid characters (# \\ % & @ ; not allowed)"),
    pincode: z
        .string().trim()
        .refine((val) => /^\d{6}$/.test(val), { message: "Pincode must be exactly 6 digits" }),
    city: z
        .string().trim().min(1, "City is required")
        .regex(/^[a-zA-Z][a-zA-Z\s().,'-]*$/, "City contains invalid characters"),
    state: z
        .string().trim().min(1, "State is required")
        .regex(/^[a-zA-Z\s\-]+$/, "State must only contain alphabetic characters"),
    orderId: z
        .string().trim().min(1, "Order ID is required")
        .refine((val) => val.trim().length > 0, { message: "Order ID cannot be only spaces" }),
    invoiceAmount: z
        .string().trim().min(1, "Invoice Amount is required")
        .refine((val) => val.trim().length > 0 && /^\d*\.?\d+$/.test(val), { message: "Invoice Amount must be a valid number" }),
    paymentMode: z
        .string().trim().min(1, "Payment Mode is required")
        .refine((val) => val.trim().length > 0, { message: "Payment Mode cannot be only spaces" }),
    productDes: z
        .string().trim().min(1, "Product Description is required")
        .refine((val) => val.trim().length > 0, { message: "Product Description cannot be only spaces" }),
    codAmount: z
        .string().optional()
        .refine((val) => !val || (val.trim().length > 0 && !/^\s+$/.test(val)), { message: "COD Amount cannot be only spaces" }),
    weight: z.preprocess(
        (val) => (val === '' || val === undefined || val === null) ? undefined : Number(val),
        z.number()
            .int("Weight must be a whole number")
            .min(50, "Weight must be at least 50 gm")
            .max(20000, "Weight must be less than or equal to 20 kg")
    ),
    length: z
        .string().min(1, "Length is required")
        .refine((val) => /^\d*\.?\d+$/.test(val) && parseFloat(val) > 0, { message: "Length must be a positive number" }),
    width: z
        .string().min(1, "Width is required")
        .refine((val) => /^\d*\.?\d+$/.test(val) && parseFloat(val) > 0, { message: "Width must be a positive number" }),
    height: z
        .string().min(1, "Height is required")
        .refine((val) => /^\d*\.?\d+$/.test(val) && parseFloat(val) > 0, { message: "Height must be a positive number" }),
});

const errorText = (msg) => (
    <p className="text-rose-500 dark:text-rose-400 text-[10px] font-medium mt-1 flex items-center gap-1 animate-[fadeIn_0.2s_ease-out]">
        <span className="w-1 h-1 rounded-full bg-rose-500 shrink-0" />
        <span>{msg}</span>
    </p>
);

const Field = ({ icon: Icon, label, error, required, endAdornment, children, isDarkMode }) => (
    <div className="flex flex-col">
        <label className={`flex items-center justify-between text-[11px] font-semibold mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-700"
            }`}>
            <span className="flex items-center gap-1">
                {label}
                {required && <span className="text-rose-500 font-bold">*</span>}
            </span>
            {endAdornment}
        </label>
        <div className="relative group">
            {Icon && (
                <Icon
                    size={11}
                    className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 pointer-events-none
                        ${isDarkMode ? "text-slate-500 group-focus-within:text-indigo-400" : "text-slate-400 group-focus-within:text-indigo-600"}`}
                />
            )}
            {children}
        </div>
        {error && errorText(error)}
    </div>
);

const SectionCard = ({ icon: Icon, iconGradient, title, subtitle, badge, children, className = "", isDarkMode }) => (
    <div
        className={`rounded-2xl border p-4 transition-all duration-200 ${isDarkMode
                ? "bg-[#121524]/90 border-white/[0.08] shadow-[0_4px_24px_rgba(0,0,0,0.25)] hover:border-indigo-500/30"
                : "bg-white border-slate-200/90 shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:border-slate-300/90 hover:shadow-[0_4px_16px_rgba(0,0,0,0.05)]"
            } ${className}`}
    >
        <div className="flex items-center justify-between pb-3 mb-3.5 border-b border-slate-100 dark:border-white/[0.06]">
            <div className="flex items-center gap-2.5">
                <span className={`w-7 h-7 rounded-xl flex items-center justify-center text-white shadow-sm shrink-0 ${iconGradient}`}>
                    <Icon size={12} />
                </span>
                <div>
                    <h2 className={`text-xs font-bold tracking-tight ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                        {title}
                    </h2>
                    {subtitle && (
                        <p className={`text-[10px] font-medium ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>
            {badge && (
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isDarkMode ? "bg-white/5 text-slate-400" : "bg-slate-100 text-slate-600"
                    }`}>
                    {badge}
                </span>
            )}
        </div>
        <div>{children}</div>
    </div>
);

/* Searchable dropdown, hoisted to module scope */
const SearchableSelect = ({ options, value, onChange, placeholder, isDarkMode, inputClassName, id }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find((opt) => opt.value === value);

    const filteredOptions = options.filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase()) ||
        (opt.pincode && String(opt.pincode).includes(search))
    );

    const handleSelect = (opt) => {
        onChange(opt);
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <div className="relative">
                <input
                    id={id}
                    type="text"
                    readOnly={!isOpen}
                    value={isOpen ? search : (selectedOption ? selectedOption.label : '')}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => { setIsOpen(true); setSearch(''); }}
                    onClick={() => { setIsOpen(true); setSearch(''); }}
                    placeholder={placeholder}
                    autoComplete="off"
                    className={`${inputClassName} cursor-pointer pr-8`}
                />
                <FaChevronDown
                    size={9}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200 ${isOpen ? 'rotate-180 text-indigo-500' : (isDarkMode ? 'text-slate-500' : 'text-slate-400')
                        }`}
                />
            </div>
            {isOpen && (
                <div
                    className={`absolute z-[10000] mt-1.5 w-full max-h-48 overflow-y-auto rounded-xl border shadow-xl custom-scrollbar animate-[fadeIn_0.15s_ease-out] ${isDarkMode
                            ? "bg-[#141829] border-white/10 shadow-black/60"
                            : "bg-white border-slate-200 shadow-slate-300/50"
                        }`}
                >
                    <div className={`px-3 py-1.5 border-b text-[9.5px] font-bold uppercase tracking-wider flex items-center justify-between ${isDarkMode ? 'border-white/[0.06] text-slate-400 bg-white/[0.02]' : 'border-slate-100 text-slate-500 bg-slate-50/70'
                        }`}>
                        <span>Select Warehouse</span>
                        <span className="text-[9px] font-mono opacity-70">{filteredOptions.length} available</span>
                    </div>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt) => (
                            <div
                                key={opt.value}
                                onMouseDown={() => handleSelect(opt)}
                                className={`px-3 py-2 text-xs cursor-pointer transition-colors flex items-center justify-between gap-2 ${isDarkMode
                                        ? "text-slate-200 hover:bg-indigo-600/20"
                                        : "text-slate-700 hover:bg-indigo-50/80"
                                    } ${opt.value === value ? (isDarkMode ? "bg-indigo-600/30 font-semibold text-indigo-300" : "bg-indigo-50 font-semibold text-indigo-600") : ""}`}
                            >
                                <div className="truncate font-medium">{opt.label}</div>
                                {opt.pincode && (
                                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 ${isDarkMode ? "bg-white/10 text-slate-300" : "bg-slate-100 text-slate-600"
                                        }`}>
                                        PIN: {opt.pincode}
                                    </span>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className={`px-3 py-4 text-center text-xs ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                            No warehouse found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default function AddShipmentPopup({ isOpen, onClose, onShipmentAdded }) {
    const { isDarkMode } = useMyContext();
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tableData, setTableData] = useState(null);
    const [wareHouseList, setwareHouseList] = useState([]);
    const [wareHouseId, setwareHouseId] = useState('');
    const [reqBody, setReqBody] = useState([]);
    const [apiError, setApiError] = useState('');

    const { register, handleSubmit, formState: { errors }, reset, watch, trigger, setValue, setError, control } = useForm({
        resolver: zodResolver(schema),
    });

    const handleClose = () => setIsPopupOpen(false);

    const pincode = watch("pincode");
    const paymentMode = watch("paymentMode");
    const city = watch("city");
    const state = watch("state");

    const lengthVal = parseFloat(watch("length")) || 0;
    const widthVal = parseFloat(watch("width")) || 0;
    const heightVal = parseFloat(watch("height")) || 0;
    const actualWeightGm = parseFloat(watch("weight")) || 0;
    const volWeightKg = (lengthVal > 0 && widthVal > 0 && heightVal > 0)
        ? ((lengthVal * widthVal * heightVal) / 5000).toFixed(2)
        : null;

    useEffect(() => {
        if (pincode && pincode.length === 6) fetchPincodeData(pincode);
    }, [pincode]);

    useEffect(() => {
        if (paymentMode === "prepaid") setValue("codAmount", "0");
        else if (paymentMode === "cod") setValue("codAmount", "");
    }, [paymentMode, setValue]);

    useEffect(() => {
        if (isOpen) {
            fetchWareHouseList();
            reset();
            setApiError('');
            setTableData(null);
        }
    }, [isOpen]);

    const fetchPincodeData = async (pincode) => {
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');
        const urlgetPincode = `${Base_Url}${getpincode}?pincode=${pincode}`;
        try {
            const response = await fetch(urlgetPincode, {
                method: 'GET',
                headers: { 'token': token, 'cus_id': cus_id, 'Content-Type': 'application/json' }
            });
            const data = await response.json();
            if (data && data.status === "SUCCESS") {
                let { city, state } = data.data[0];
                city = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
                state = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
                setValue("city", city);
                setValue("state", state);
            } else if (data && data.status === "FAIL") {
                setValue("city", "");
                setValue("state", "");
                toast.error(data.message);
            } else {
                toast.error("Failed to fetch city and state data");
            }
        } catch (error) {
            console.error('Error fetching pincode data:', error);
            toast.error("Error fetching pincode data");
        }
    };

    const handleInputChange = (event) => {
        const { value, id } = event.target;
        if (['pincode', 'mobile', 'boxQty', 'orderId'].includes(id)) {
            const filteredValue = value.replace(/[^0-9]/g, '');
            event.target.value = filteredValue;
            if (id === "pincode") {
                if (filteredValue.length < 6) {
                    setValue("city", "");
                    setValue("state", "");
                } else if (filteredValue.length === 6) {
                    fetchPincodeData(filteredValue);
                }
            }
        }
        if (['firstName', 'lastName', 'city', 'state'].includes(id)) {
            const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
            event.target.value = filteredValue;
        }
    };

    const fetchWareHouseList = async () => {
        const token = localStorage.getItem('token');
        const cusid = localStorage.getItem('cus_id');
        const headers = new Headers({ 'token': token, 'cus_id': cusid, 'Content-Type': 'application/json' });
        try {
            const response = await fetch(Base_Url + allwarehouseslist, { method: 'GET', headers });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            const transformedData = data.data.map(warehouse => ({
                value: warehouse.id, label: warehouse.name, pincode: warehouse.pincode
            }));
            setwareHouseList(transformedData);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const generateStandardOrderId = () => {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        const rand = Math.floor(10000 + Math.random() * 90000);
        return `${ts}${rand}`;
    };

    const mapKeyToField = (key) => {
        const FIELD_MAP = {
            'customer.email': 'email',
            'customer.first_name': 'firstName',
            'customer.last_name': 'lastName',
            'customer.phone': 'mobile',
            'droplocation.address': 'address',
            'droplocation.pin': 'pincode',
            'droplocation.city': 'city',
            'droplocation.state': 'state',
            'pickuplocation.warehouse_id': 'warehouse',
            'product.order_id': 'orderId',
            'product.product_desc': 'productDes',
            'product.total_amount': 'invoiceAmount',
            'product.cod_amount': 'codAmount',
            'product.payment_mode': 'paymentMode',
            'dimension.length': 'length',
            'dimension.width': 'width',
            'dimension.height': 'height',
            'dimension.weight': 'weight',
        };

        if (FIELD_MAP[key]) return FIELD_MAP[key];
        const lower = String(key || '').toLowerCase();
        for (const [k, f] of Object.entries(FIELD_MAP)) {
            if (k.toLowerCase() === lower) return f;
        }
        if (lower.includes('email')) return 'email';
        if (lower.includes('phone') || lower.includes('mobile')) return 'mobile';
        if (lower.includes('first_name') || lower.includes('firstname')) return 'firstName';
        if (lower.includes('last_name') || lower.includes('lastname')) return 'lastName';
        if (lower.includes('pin')) return 'pincode';
        if (lower.includes('city')) return 'city';
        if (lower.includes('state')) return 'state';
        if (lower.includes('address')) return 'address';
        if (lower.includes('warehouse')) return 'warehouse';
        if (lower.includes('order_id') || lower.includes('orderid')) return 'orderId';
        if (lower.includes('weight')) return 'weight';
        if (lower.includes('length')) return 'length';
        if (lower.includes('width')) return 'width';
        if (lower.includes('height')) return 'height';
        if (lower.includes('payment_mode')) return 'paymentMode';
        if (lower.includes('cod')) return 'codAmount';
        if (lower.includes('amount') || lower.includes('invoice')) return 'invoiceAmount';
        if (lower.includes('desc')) return 'productDes';
        return null;
    };

    const handleServerErrors = (errorData) => {
        const errorList = [];
        const messages = errorData?.messages;
        if (messages && typeof messages === 'object' && !Array.isArray(messages)) {
            Object.entries(messages).forEach(([key, msg]) => {
                const errorMsg = Array.isArray(msg) ? msg.join(', ') : String(msg);
                errorList.push(errorMsg);
                const targetField = mapKeyToField(key);
                if (targetField) {
                    setError(targetField, { type: 'server', message: errorMsg });
                }
            });
        } else if (typeof messages === 'string') {
            errorList.push(messages);
        } else if (errorData?.message) {
            errorList.push(errorData.message);
        }
        return errorList;
    };

    const onInvalid = (formErrors) => {
        const firstKey = Object.keys(formErrors)[0];
        if (firstKey && formErrors[firstKey]?.message) {
            toast.error(formErrors[firstKey].message);
        }
    };

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        setApiError('');
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');
        const orderIdVal = data.orderId || generateStandardOrderId();
        const requestBody = {
            order_id: orderIdVal,
            customer: {
                first_name: data.firstName,
                last_name: data.lastName,
                email: data.email,
                phone: data.mobile,
                address_type: "home",
            },
            droplocation: {
                address: data.address,
                city: data.city,
                state: data.state,
                country: "India",
                pin: data.pincode,
            },
            pickuplocation: { warehouse_id: wareHouseId },
            product: {
                order_id: orderIdVal,
                product_desc: data.productDes,
                quantity: "1",
                total_amount: data.invoiceAmount,
                cod_amount: data.codAmount,
                payment_mode: data.paymentMode,
                shipment_type: "Forward"
            },
            dimension: {
                length: data.length,
                width: data.width,
                height: data.height,
                weight: data.weight,
            },
        };
        setReqBody(requestBody);
        try {
            const response = await axios.post(Base_Url + addShipment, requestBody, {
                headers: { 'Content-Type': 'application/json', 'token': token, 'cus_id': cus_id },
            });

            const isSuccess = response.data &&
                (response.data.status === "SUCCESS" || response.data.status === 200) &&
                response.data.status !== "FAIL" &&
                response.data.status !== "ERROR" &&
                !response.data.error;

            if (isSuccess) {
                toast.success(response.data.message || "Shipment added successfully!");
                setTableData(response.data);
                reset();
                if (onShipmentAdded) {
                    onShipmentAdded(orderIdVal, data, response.data);
                }
                onClose();
            } else {
                const errs = handleServerErrors(response.data);
                const msg = errs.length > 0 ? errs.join(' | ') : (response.data?.message || "Failed to add shipment");
                setApiError(msg);
                toast.error(msg);
            }
        } catch (error) {
            console.error("API request failed:", error);
            const errData = error.response?.data;
            const errs = handleServerErrors(errData);
            const fallbackMsg = errs.length > 0
                ? errs.join(' | ')
                : (errData?.message || error.message || "Failed to add shipment");
            setApiError(fallbackMsg);
            toast.error(fallbackMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputBase = `w-full pl-8 pr-3 py-2 text-xs font-medium rounded-xl border transition-all duration-200 outline-none
        placeholder:text-slate-400 dark:placeholder:text-slate-500
        ${isDarkMode
            ? "bg-white/[0.04] border-white/10 text-white focus:bg-[#161a29] focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20 hover:border-white/20"
            : "bg-slate-50/80 border-slate-200 text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15 hover:border-slate-300"
        }`;

    return (
        <>
            {/* Overlay */}
            <div
                onClick={onClose}
                className={`fixed inset-0 bg-slate-900/40 dark:bg-black/75 backdrop-blur-[3px] z-[9998] transition-opacity duration-300
                    ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            />

            {/* Right side sliding drawer */}
            <div
                className={`fixed top-0 right-0 h-full w-full sm:w-[560px] md:w-[620px] lg:w-[660px] z-[9999]
                    ${isDarkMode
                        ? "bg-[#0c0e17] text-slate-100 border-l border-indigo-500/20 shadow-[0_0_60px_rgba(79,70,229,0.25)]"
                        : "bg-slate-50 text-slate-800 border-l border-slate-200/90 shadow-[0_0_50px_rgba(0,0,0,0.12)]"
                    }
                    shadow-2xl rounded-tl-2xl rounded-bl-2xl transform transition-transform duration-300 ease-in-out
                    ${isOpen ? "translate-x-0" : "translate-x-full"}
                    flex flex-col overflow-hidden`}
            >
                {/* Drawer Header */}
                <div className={`flex items-center justify-between px-5 py-3.5 border-b shrink-0 ${isDarkMode
                        ? "bg-[#0c0e17]/95 backdrop-blur-md border-white/[0.08]"
                        : "bg-white/95 backdrop-blur-md border-slate-200/90"
                    }`}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-violet-600 flex items-center justify-center text-white shadow-md shadow-indigo-500/20 shrink-0">
                            <FaBoxOpen size={18} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-extrabold tracking-wider uppercase ${isDarkMode
                                        ? "bg-indigo-500/15 text-indigo-300 border border-indigo-500/30"
                                        : "bg-indigo-50 text-indigo-700 border border-indigo-200/80"
                                    }`}>
                                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                                    B2C · Express Booking
                                </span>
                            </div>
                            <h1 className={`text-base font-bold tracking-tight ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                                Create Forward Shipment
                            </h1>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        type="button"
                        aria-label="Close drawer"
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${isDarkMode
                                ? "bg-white/[0.06] hover:bg-white/[0.12] text-slate-400 hover:text-white border border-white/[0.08]"
                                : "bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 border border-slate-200"
                            }`}
                    >
                        <RxCross2 size={16} />
                    </button>
                </div>

                {/* Drawer Body Scroll Area */}
                <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar">
                    <form id="forward-shipment-form" onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
                        {apiError && (
                            <div role="alert" className={`flex items-start gap-2 rounded-xl border p-3 text-xs font-medium shadow-sm ${isDarkMode ? 'bg-rose-950/40 border-rose-500/40 text-rose-300' : 'bg-rose-50 border-rose-200 text-rose-700'
                                }`}>
                                <span className="flex-1">{apiError}</span>
                                <button type="button" onClick={() => setApiError('')} className="opacity-60 hover:opacity-100 font-bold" aria-label="Dismiss">×</button>
                            </div>
                        )}

                        {/* SECTION 1: CUSTOMER & PICKUP DETAILS */}
                        <SectionCard
                            icon={FaUser}
                            iconGradient="bg-gradient-to-tr from-indigo-500 to-violet-600"
                            title="Customer & Pickup Details"
                            subtitle="Warehouse source & recipient destination"
                            badge="Step 1 of 3"
                            isDarkMode={isDarkMode}
                        >
                            <div className="space-y-3">
                                {/* Warehouse - full row for clear view */}
                                <Field
                                    icon={FaWarehouse}
                                    label="Pickup Warehouse"
                                    required
                                    error={errors.warehouse?.message}
                                    isDarkMode={isDarkMode}
                                >
                                    <Controller
                                        name="warehouse"
                                        control={control}
                                        render={({ field }) => (
                                            <SearchableSelect
                                                id="warehouse"
                                                options={wareHouseList}
                                                value={field.value}
                                                onChange={(selected) => {
                                                    field.onChange(selected.value);
                                                    setwareHouseId(selected.value);
                                                    trigger("warehouse");
                                                }}
                                                placeholder="Search & select pickup warehouse"
                                                isDarkMode={isDarkMode}
                                                inputClassName={`${inputBase} ${errors.warehouse ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                            />
                                        )}
                                    />
                                </Field>

                                {/* Name row: First Name + Last Name */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field
                                        icon={FaUser}
                                        label="First Name"
                                        required
                                        error={errors.firstName?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="firstName"
                                            {...register("firstName", { onChange: handleInputChange })}
                                            placeholder="Enter first name"
                                            className={`${inputBase} ${errors.firstName ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        />
                                    </Field>

                                    <Field
                                        icon={FaUser}
                                        label="Last Name"
                                        required
                                        error={errors.lastName?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="lastName"
                                            {...register("lastName", { onChange: handleInputChange })}
                                            placeholder="Enter last name"
                                            className={`${inputBase} ${errors.lastName ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        />
                                    </Field>
                                </div>

                                {/* Contact row: Mobile + Email */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field
                                        icon={FaPhoneAlt}
                                        label="Mobile Number"
                                        required
                                        error={errors.mobile?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="mobile"
                                            {...register("mobile", { onChange: handleInputChange })}
                                            placeholder="10-digit mobile number"
                                            maxLength={10}
                                            className={`${inputBase} ${errors.mobile ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        />
                                    </Field>

                                    <Field
                                        icon={FaEnvelope}
                                        label="Email Address"
                                        required
                                        error={errors.email?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="email"
                                            {...register("email")}
                                            placeholder="customer@domain.com"
                                            type="email"
                                            className={`${inputBase} ${errors.email ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        />
                                    </Field>
                                </div>

                                {/* Location row: Pincode + City + State */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <Field
                                        icon={FaHashtag}
                                        label="Pincode"
                                        required
                                        error={errors.pincode?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="pincode"
                                            {...register("pincode", { onChange: handleInputChange })}
                                            placeholder="6-digit PIN"
                                            maxLength={6}
                                            className={`${inputBase} font-mono ${errors.pincode ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        />
                                    </Field>

                                    <Field
                                        icon={FaCity}
                                        label="City"
                                        required
                                        endAdornment={city ? <FaCheckCircle size={10} className="text-emerald-500" /> : null}
                                        error={errors.city?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="city"
                                            {...register("city", { onChange: handleInputChange })}
                                            placeholder="Auto-detected city"
                                            className={`${inputBase} ${errors.city ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        />
                                    </Field>

                                    <Field
                                        icon={FaFlag}
                                        label="State"
                                        required
                                        endAdornment={state ? <FaCheckCircle size={10} className="text-emerald-500" /> : null}
                                        error={errors.state?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="state"
                                            {...register("state", { onChange: handleInputChange })}
                                            placeholder="Auto-detected state"
                                            className={`${inputBase} ${errors.state ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        />
                                    </Field>
                                </div>

                                {/* Address - full width */}
                                <Field
                                    icon={FaMapMarkerAlt}
                                    label="Complete Delivery Address"
                                    required
                                    error={errors.address?.message}
                                    isDarkMode={isDarkMode}
                                >
                                    <input
                                        id="address"
                                        {...register("address")}
                                        placeholder="House / Flat No., Street, Landmark, Area"
                                        className={`${inputBase} ${errors.address ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                    />
                                </Field>
                            </div>
                        </SectionCard>

                        {/* SECTION 2: ORDER & PAYMENT DETAILS */}
                        <SectionCard
                            icon={FaShoppingCart}
                            iconGradient="bg-gradient-to-tr from-blue-600 to-cyan-600"
                            title="Order & Payment Info"
                            subtitle="Commercial value & payment terms"
                            badge="Step 2 of 3"
                            isDarkMode={isDarkMode}
                        >
                            <div className="space-y-3">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field
                                        icon={FaHashtag}
                                        label="Order ID / Reference"
                                        required
                                        error={errors.orderId?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="orderId"
                                            {...register("orderId", { onChange: handleInputChange })}
                                            placeholder="e.g. ORD-98214"
                                            className={`${inputBase} font-mono ${errors.orderId ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        />
                                    </Field>

                                    <Field
                                        icon={FaRupeeSign}
                                        label="Total Invoice Amount"
                                        required
                                        error={errors.invoiceAmount?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="invoiceAmount"
                                            {...register("invoiceAmount")}
                                            placeholder="0.00"
                                            className={`${inputBase} font-semibold ${errors.invoiceAmount ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        />
                                    </Field>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field
                                        icon={FaWallet}
                                        label="Payment Mode"
                                        required
                                        error={errors.paymentMode?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <select
                                            id="paymentMode"
                                            {...register("paymentMode")}
                                            className={`${inputBase} cursor-pointer pr-8 ${errors.paymentMode ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        >
                                            <option value="" className={isDarkMode ? "bg-[#141829]" : ""}>Select Payment Mode</option>
                                            <option value="prepaid" className={isDarkMode ? "bg-[#141829]" : ""}>Prepaid</option>
                                            <option value="cod" className={isDarkMode ? "bg-[#141829]" : ""}>Cash on Delivery (COD)</option>
                                        </select>
                                    </Field>

                                    <Field
                                        icon={FaRupeeSign}
                                        label="COD Collection Amount"
                                        error={errors.codAmount?.message}
                                        endAdornment={
                                            paymentMode === "cod" ? (
                                                <span className="text-[9px] font-bold text-amber-500 uppercase tracking-wide">COD Active</span>
                                            ) : (
                                                <span className="text-[9px] text-slate-400">Prepaid = ₹0</span>
                                            )
                                        }
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="codAmount"
                                            {...register("codAmount")}
                                            disabled={paymentMode !== "cod"}
                                            placeholder={paymentMode === "cod" ? "Enter COD amount" : "Prepaid order (₹0)"}
                                            className={`${inputBase} font-semibold ${paymentMode !== "cod"
                                                    ? "opacity-50 cursor-not-allowed bg-slate-100 dark:bg-white/[0.02]"
                                                    : "border-amber-500/50 dark:border-amber-500/50"
                                                } ${errors.codAmount ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        />
                                    </Field>
                                </div>

                                <Field
                                    icon={FaClipboardList}
                                    label="Product Description"
                                    required
                                    error={errors.productDes?.message}
                                    isDarkMode={isDarkMode}
                                >
                                    <input
                                        id="productDes"
                                        {...register("productDes")}
                                        placeholder="e.g. Cotton T-Shirt, Electronics, Books"
                                        className={`${inputBase} ${errors.productDes ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                    />
                                </Field>
                            </div>
                        </SectionCard>

                        {/* SECTION 3: PACKAGE DETAILS */}
                        <SectionCard
                            icon={FaBox}
                            iconGradient="bg-gradient-to-tr from-amber-500 to-orange-500"
                            title="Package Specifications"
                            subtitle="Dead weight & dimensional parameters"
                            badge="Step 3 of 3"
                            isDarkMode={isDarkMode}
                        >
                            <div className="space-y-3">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field
                                        icon={FaWeightHanging}
                                        label="Package Weight"
                                        required
                                        endAdornment={<span className="text-[10px] font-bold text-indigo-500">gm (50 - 10,000)</span>}
                                        error={errors.weight?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="weight"
                                            {...register("weight")}
                                            type="number"
                                            placeholder="Enter weight in grams"
                                            step="1"
                                            className={`${inputBase} font-semibold ${errors.weight ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        />
                                    </Field>

                                    <Field
                                        icon={FaBoxOpen}
                                        label="Box Quantity"
                                        endAdornment={<span className="text-[10px] text-slate-400 flex items-center gap-1"><FaLock size={8} /> Fixed</span>}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            value="1 Box"
                                            disabled
                                            className={`${inputBase} opacity-60 cursor-not-allowed bg-slate-100 dark:bg-white/[0.02] font-medium`}
                                        />
                                    </Field>
                                </div>

                                {/* Dimensions L x W x H */}
                                <div>
                                    <label className={`block text-[11px] font-semibold mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-700"
                                        }`}>
                                        Dimensions <span className="text-rose-500">*</span> <span className="text-[10px] font-normal text-slate-400">(Length × Width × Height in cm)</span>
                                    </label>
                                    <div className="grid grid-cols-3 gap-2.5">
                                        <div className="relative group">
                                            <FaRulerCombined size={10} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                            <input
                                                id="length"
                                                placeholder="L (cm)"
                                                {...register("length")}
                                                className={`${inputBase} pl-7 pr-2 font-mono text-center ${errors.length ? '!border-rose-500/80' : ''}`}
                                            />
                                            {errors.length && errorText(errors.length?.message)}
                                        </div>

                                        <div className="relative group">
                                            <FaRulerCombined size={10} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                            <input
                                                id="width"
                                                placeholder="W (cm)"
                                                {...register("width")}
                                                className={`${inputBase} pl-7 pr-2 font-mono text-center ${errors.width ? '!border-rose-500/80' : ''}`}
                                            />
                                            {errors.width && errorText(errors.width?.message)}
                                        </div>

                                        <div className="relative group">
                                            <FaRulerCombined size={10} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                            <input
                                                id="height"
                                                placeholder="H (cm)"
                                                {...register("height")}
                                                className={`${inputBase} pl-7 pr-2 font-mono text-center ${errors.height ? '!border-rose-500/80' : ''}`}
                                            />
                                            {errors.height && errorText(errors.height?.message)}
                                        </div>
                                    </div>
                                </div>

                                {/* Live Volumetric Weight calculation helper */}
                                <div className={`p-2.5 rounded-xl border flex items-center justify-between text-xs ${isDarkMode
                                        ? "bg-indigo-950/20 border-indigo-500/20 text-indigo-200"
                                        : "bg-indigo-50/60 border-indigo-100 text-indigo-900"
                                    }`}>
                                    <div className="flex items-center gap-2">
                                        <span className="w-6 h-6 rounded-lg bg-indigo-500/10 text-indigo-500 flex items-center justify-center shrink-0">
                                            <FaBox size={11} />
                                        </span>
                                        <div>
                                            <span className="font-semibold block text-[11px]">Volumetric Weight</span>
                                            <span className="text-[10px] text-slate-500 dark:text-slate-400">Formula: (L × W × H) ÷ 5000</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="font-extrabold font-mono text-xs text-indigo-600 dark:text-indigo-400">
                                            {volWeightKg ? `${volWeightKg} kg` : '—'}
                                        </span>
                                        {actualWeightGm > 0 && (
                                            <span className="block text-[9px] text-slate-500">
                                                Actual: {(actualWeightGm / 1000).toFixed(2)} kg
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </SectionCard>
                    </form>
                </div>

                {/* Sticky Action Footer */}
                <div className={`sticky bottom-0 z-30 px-5 py-3.5 border-t shrink-0 flex items-center justify-between gap-3 ${isDarkMode
                        ? "bg-[#0c0e17]/95 backdrop-blur-md border-white/[0.08]"
                        : "bg-white/95 backdrop-blur-md border-slate-200/90"
                    }`}>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className={`px-4 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-200 ${isDarkMode
                                ? "border-white/10 text-slate-300 hover:bg-white/[0.06] hover:text-white"
                                : "border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                            }`}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="forward-shipment-form"
                        disabled={isSubmitting}
                        className={`flex-1 py-2.5 px-5 rounded-xl font-bold text-xs text-white flex items-center justify-center gap-2 shadow-lg transition-all duration-200 ${isDarkMode
                                ? "bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:from-blue-500 hover:via-indigo-500 hover:to-violet-500 shadow-indigo-500/25 active:scale-[0.99]"
                                : "bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:from-blue-700 hover:via-indigo-700 hover:to-violet-700 shadow-blue-500/25 active:scale-[0.99]"
                            } ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""}`}
                    >
                        {isSubmitting ? (
                            <span className="flex items-center gap-2">
                                <Spinner /> Creating Shipment...
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                <FaBoxOpen size={13} />
                                <span>Create & Book Shipment</span>
                            </span>
                        )}
                    </button>
                </div>
            </div>

            <ViewRateForword
                isopen={isPopupOpen}
                onClose={handleClose}
                data={tableData}
                reqBody={reqBody}
            />
        </>
    );
}